/**
 * „Später erinnern“ (I-2915): the instants a notification can be put off to.
 * All in the user's local time; master hides the notification until then on
 * every device (`POST /notifications/:id/snooze`).
 */

export type NotificationSnoozePresetId =
  | "hour"
  | "evening"
  | "tomorrow"
  | "nextWeek";

export interface NotificationSnoozePreset {
  id: NotificationSnoozePresetId;
  /** When the notification comes back. */
  until: Date;
}

/** The hour „this evening“ means, and the hour a morning starts. */
const EVENING_HOUR = 18;
const MORNING_HOUR = 8;

function atHour(day: Date, addDays: number, hour: number): Date {
  return new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate() + addDays,
    hour,
    0,
    0,
    0,
  );
}

/**
 * The presets as of `now`: in one hour, this evening at 18:00 (left out once
 * that has passed), tomorrow at 8:00, and Monday of next week at 8:00 (left
 * out on a Sunday, when that is tomorrow).
 */
export function notificationSnoozePresets(
  now: Date = new Date(),
): NotificationSnoozePreset[] {
  const presets: NotificationSnoozePreset[] = [
    { id: "hour", until: new Date(now.getTime() + 60 * 60 * 1000) },
  ];
  const evening = atHour(now, 0, EVENING_HOUR);
  if (evening.getTime() > now.getTime()) {
    presets.push({ id: "evening", until: evening });
  }
  presets.push({ id: "tomorrow", until: atHour(now, 1, MORNING_HOUR) });
  // getDay(): Sunday 0 … Saturday 6. The Monday after this week's — on a
  // Monday that is seven days on, on a Sunday the next day.
  const toMonday = (8 - now.getDay()) % 7 || 7;
  // On a Sunday that Monday is tomorrow: one entry says it.
  if (toMonday > 1) {
    presets.push({
      id: "nextWeek",
      until: atHour(now, toMonday, MORNING_HOUR),
    });
  }
  return presets;
}
